import React, { useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { isSameDay } from '@features/agenda/utils/agenda-dates';
import { eventColor } from '../event-color';
import { computeEventLayouts } from '../event-layout';
import { DAY_START_HOUR, eventTimeLabel, nowOffset } from '../time-grid';
import { Text, UnstyledButton } from '@/shared/ui';

interface MobileDayViewProps {
  date: Date;
  events: ExpandedEvent[];
  calendars: Calendar[];
  onSelect: (event: ExpandedEvent) => void;
  onSlotClick?: (date: Date) => void;
}

const SLOT_HEIGHT = 60;
const HOURS = Array.from({ length: 24 }, (_, i) => i + DAY_START_HOUR);

/**
 * Single-day vertical timeline optimised for mobile touch targets.
 * Events render with larger padding and wrapping titles.
 *
 * @param props Date, events, calendars and select handler.
 * @returns Rendered mobile day view.
 */
export const MobileDayView: React.FC<MobileDayViewProps> = ({
  date,
  events,
  calendars,
  onSelect,
  onSlotClick,
}) => {
  const { t } = useTranslation('features/agenda');
  const dayEvents = events.filter((e) => isSameDay(e.start_at_utc, date));
  const now = nowOffset(date, SLOT_HEIGHT, 24);
  const layouts = computeEventLayouts(dayEvents);
  const LEFT_OFFSET = 56;
  const RIGHT_MARGIN = 12;
  const GUTTER = 2;
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (now !== null && scrollRef.current) {
      scrollRef.current.scrollTop = Math.max(0, now - scrollRef.current.clientHeight / 3);
    }
  }, [now]);

  return (
    <div
      data-testid="agenda-mobile-day"
      ref={scrollRef}
      className="h-full overflow-y-auto"
      style={{ paddingBottom: 'calc(var(--space-16) + env(safe-area-inset-bottom, 0px))' }}
    >
      <div className="relative" style={{ height: HOURS.length * SLOT_HEIGHT }}>
        {HOURS.map((h) => (
          <div
            key={h}
            className="absolute inset-x-0 flex"
            style={{ top: (h - DAY_START_HOUR) * SLOT_HEIGHT, height: SLOT_HEIGHT }}
          >
            <div
              className="w-12 shrink-0 pr-(--space-2) pt-px text-right text-[length:var(--text-micro)] font-medium text-[var(--text-tertiary)]"
            >
              {String(h).padStart(2, '0')}:00
            </div>
            <UnstyledButton
              type="button"
              aria-label={`${String(h).padStart(2, '0')}:00`}
              onClick={() => {
                if (!onSlotClick) return;
                const d = new Date(date);
                d.setHours(h, 0, 0, 0);
                onSlotClick(d);
              }}
              className="flex-1 border-t border-[var(--border-ultra-subtle)]"
              style={{ height: SLOT_HEIGHT }}
            >
              {null}
            </UnstyledButton>
          </div>
        ))}

        {now !== null && (
          <div
            data-testid="agenda-now-line"
            aria-hidden="true"
            className="pointer-events-none absolute z-10 flex items-center"
            style={{ top: now, left: 48, right: 'var(--space-2)' }}
          >
            <Text component="span" className="h-2.5 w-2.5 -ml-1 rounded-full bg-[var(--status-error)]" />
            <Text component="span" className="h-px flex-1 bg-[var(--status-error)]" />
          </div>
        )}

        {dayEvents.map((ev) => {
          const color = eventColor(ev, calendars);
          const startMin = dayjs(ev.start_at_utc).hour() * 60 + dayjs(ev.start_at_utc).minute();
          const endMin = dayjs(ev.end_at_utc).hour() * 60 + dayjs(ev.end_at_utc).minute();
          const rawTop = (startMin - DAY_START_HOUR * 60) * (SLOT_HEIGHT / 60);
          const rawHeight = (endMin - startMin) * (SLOT_HEIGHT / 60);
          // Clip events starting before DAY_START_HOUR to the grid's start
          // instead of letting them render above it (unreachable/cut off).
          const top = Math.max(0, rawTop);
          const height = Math.max(rawHeight + Math.min(0, rawTop), 32);
          const { colIndex, totalCols } = layouts.get(
            `${ev.event_id}:${ev.occurrence_start}`
          ) ?? { colIndex: 0, totalCols: 1 };
          const availableWidth = `calc(100% - ${LEFT_OFFSET + RIGHT_MARGIN}px)`;
          const colWidth = `calc((${availableWidth}) / ${totalCols} - ${GUTTER}px)`;
          const colLeft = `calc(${LEFT_OFFSET}px + (${availableWidth}) / ${totalCols} * ${colIndex} + ${GUTTER}px)`;
          return (
            <UnstyledButton
              key={`${ev.event_id}-${ev.occurrence_start}`}
              type="button"
              data-testid="agenda-mobile-event-chip"
              onClick={() => onSelect(ev)}
              className="absolute cursor-pointer overflow-hidden rounded-[var(--radius-md)] border-l-[3px] px-(--space-2) py-(--space-1) text-left transition-shadow active:opacity-80"
              style={{
                top,
                minHeight: height,
                left: colLeft,
                width: colWidth,
                borderColor: color,
                background: `color-mix(in srgb, ${color} 10%, var(--surface-primary))`,
              }}
            >
              <Text className="truncate text-xs font-bold" style={{ color }}>
                {eventTimeLabel(ev, t('drawer.all_day'))}
              </Text>
              <Text className="truncate text-xs font-semibold text-[var(--text-primary)] leading-snug">
                {ev.title}
              </Text>
            </UnstyledButton>
          );
        })}
      </div>
    </div>
  );
};
